import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Upload,
    Download,
    Loader2,
    Image as ImageIcon,
    CreditCard,
    LogOut,
    Crown,
    X,
    Menu,
    User
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
    const { user, logout, refreshUser } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [dragOver, setDragOver] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [processedImage, setProcessedImage] = useState(null);
    const [processedFilename, setProcessedFilename] = useState('');
    const [history, setHistory] = useState([]);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [batchMode, setBatchMode] = useState(false);
    const [batchProcessing, setBatchProcessing] = useState(false);
    const [batchResults, setBatchResults] = useState(null);
    const [processingProgress, setProcessingProgress] = useState(0);

    const businessName = settings?.business_name || 'BGRemove';
    const logoUrl = settings?.logo_url;
    const isProUser = user?.subscription_status === 'active';
    const creditsAvailable = user?.credits_available || 0;
    const subscriptionPlan = user?.subscription_plan || null;
    const monthlyCredits = user?.subscription_credits_monthly || 0;

    useEffect(() => {
        fetchHistory();
    }, []);

    const fetchHistory = async () => {
        try {
            const response = await axios.get(`${API}/images/history`);
            setHistory(response.data);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    const canUpload = () => {
        return creditsAvailable > 0;
    };

    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        setDragOver(false);

        // Get all files
        const files = Array.from(e.dataTransfer?.files || e.target?.files || []);
        if (files.length === 0) return;

        // Filter to images only
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        if (imageFiles.length === 0) {
            toast.error('Please upload image files');
            return;
        }

        if (imageFiles.length !== files.length) {
            toast.warning(`${files.length - imageFiles.length} non-image file(s) skipped`);
        }

        // Handle multiple files (batch mode)
        if (imageFiles.length > 1) {
            // Check if Pro user
            if (!isProUser) {
                toast.error('Batch processing is only available for Pro users!');
                navigate('/pricing');
                return;
            }

            // Enable batch mode
            setSelectedFiles(imageFiles);
            setBatchMode(true);
            setProcessedImage(null);
            setBatchResults(null);
            return;
        }

        // Single file - existing flow
        const file = imageFiles[0];
        if (!canUpload()) {
            toast.error('No credits available. Please purchase credits or subscribe to a plan!');
            navigate('/pricing');
            return;
        }

        await processImage(file);
    }, [user, navigate, creditsAvailable]);

    const processImage = async (file) => {
        setProcessing(true);
        setProcessedImage(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API}/images/remove-background`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setProcessedImage(response.data.image_data);
            setProcessedFilename(response.data.filename);
            toast.success('Background removed successfully!');
            await refreshUser();
            await fetchHistory();
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to process image';
            toast.error(message);
            if (message.includes('limit reached')) {
                navigate('/pricing');
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleFileInput = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleDrop({
                dataTransfer: { files: Array.from(files) },
                preventDefault: () => {}
            });
        }
    };

    const downloadImage = () => {
        if (!processedImage) return;

        const link = document.createElement('a');
        link.href = processedImage;
        link.download = processedFilename || 'bg_removed.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Image downloaded!');
    };

    const processBatch = async () => {
        if (selectedFiles.length === 0) return;

        setBatchProcessing(true);
        setProcessingProgress(0);
        setBatchResults(null);

        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('files', file);
        });

        try {
            const response = await axios.post(`${API}/images/batch-remove-background`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setProcessingProgress(progress);
                }
            });

            setBatchResults(response.data);
            toast.success(`Processed ${response.data.successful} out of ${response.data.total_images} images!`);
            await refreshUser();
            await fetchHistory();
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to process batch';
            toast.error(message);
            if (message.includes('Pro subscribers')) {
                navigate('/pricing');
            }
        } finally {
            setBatchProcessing(false);
            setProcessingProgress(0);
        }
    };

    const cancelBatch = () => {
        setSelectedFiles([]);
        setBatchMode(false);
        setBatchResults(null);
        setProcessingProgress(0);
    };

    const downloadBatchZip = (zipData) => {
        if (!zipData) return;

        try {
            // Convert base64 to blob
            const byteCharacters = atob(zipData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/zip' });

            // Create download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'bg_removed_images.zip';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success('ZIP downloaded!');
        } catch (error) {
            console.error('Error downloading ZIP:', error);
            toast.error('Failed to download ZIP');
        }
    };

    const downloadIndividualImage = (imageData, filename) => {
        if (!imageData) return;

        const link = document.createElement('a');
        link.href = imageData;
        link.download = `bg_removed_${filename}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Image downloaded!');
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        toast.success('Logged out successfully');
    };

    // Credits display logic
    const creditsDisplay = subscriptionPlan
        ? `${monthlyCredits} credits/month`
        : `${creditsAvailable} credits`;

    return (
        <div className="min-h-screen bg-background">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo-link">
                            {logoUrl ? (
                                <img 
                                    src={logoUrl.startsWith('http') ? logoUrl : `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`}
                                    alt={businessName}
                                    className="w-8 h-8 rounded-lg object-contain"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                    <Upload className="w-4 h-4 text-primary-foreground" />
                                </div>
                            )}
                            <span className="font-heading font-bold text-xl tracking-tight">{businessName}</span>
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden md:flex items-center gap-4">
                            {!isProUser && (
                                <Button 
                                    variant="outline" 
                                    className="rounded-full"
                                    onClick={() => navigate('/pricing')}
                                    data-testid="dashboard-upgrade-btn"
                                >
                                    <Crown className="w-4 h-4 mr-2 text-primary" />
                                    Upgrade to Pro
                                </Button>
                            )}
                            
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="rounded-full h-10 w-10 p-0" data-testid="dashboard-user-menu">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <User className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-3 py-2">
                                        <p className="font-medium truncate">{user?.name}</p>
                                        <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/account')} data-testid="dropdown-account">
                                        <CreditCard className="w-4 h-4 mr-2" />
                                        Account
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="dropdown-logout">
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        {/* Mobile menu button */}
                        <button 
                            className="md:hidden p-2" 
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="dashboard-mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-border bg-background p-4 space-y-3">
                        <div className="pb-3 border-b border-border">
                            <p className="font-medium">{user?.name}</p>
                            <p className="text-sm text-muted-foreground">{user?.email}</p>
                        </div>
                        {!isProUser && (
                            <Button 
                                className="w-full rounded-full"
                                onClick={() => navigate('/pricing')}
                                data-testid="mobile-upgrade-btn"
                            >
                                <Crown className="w-4 h-4 mr-2" />
                                Upgrade to Pro
                            </Button>
                        )}
                        <Button 
                            variant="outline" 
                            className="w-full rounded-full"
                            onClick={() => navigate('/account')}
                            data-testid="mobile-account-btn"
                        >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Account
                        </Button>
                        <Button 
                            variant="ghost" 
                            className="w-full rounded-full text-destructive"
                            onClick={handleLogout}
                            data-testid="mobile-logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                )}
            </nav>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    {/* Status Bar */}
                    <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">
                                Welcome back, {user?.name?.split(' ')[0]}
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                {subscriptionPlan ? (
                                    <span className="inline-flex items-center gap-1">
                                        <Crown className="w-4 h-4 text-primary" />
                                        {creditsDisplay}
                                    </span>
                                ) : (
                                    creditsDisplay
                                )}
                            </p>
                        </div>
                        {isProUser && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium" data-testid="pro-badge">
                                <Crown className="w-4 h-4 mr-1" />
                                Pro
                            </span>
                        )}
                    </div>

                    {/* Upload Zone */}
                    <div
                        className={`upload-zone p-12 text-center mb-8 ${!batchMode && !batchProcessing ? 'cursor-pointer' : ''} ${dragOver ? 'drag-over border-primary' : 'border-border'}`}
                        onDragOver={(e) => { e.preventDefault(); if (!batchProcessing) setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={batchProcessing ? (e) => e.preventDefault() : handleDrop}
                        onClick={!batchMode && !batchProcessing ? () => document.getElementById('file-input')?.click() : undefined}
                        data-testid="upload-zone"
                    >
                        <input
                            id="file-input"
                            type="file"
                            accept="image/*"
                            multiple={isProUser}
                            onChange={handleFileInput}
                            className="hidden"
                            data-testid="file-input"
                        />

                        {processing || batchProcessing ? (
                            <div className="py-8">
                                <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary mb-4" />
                                <p className="text-lg font-medium">
                                    {batchProcessing ? `Processing ${selectedFiles.length} images...` : 'Processing your image...'}
                                </p>
                                {batchProcessing && processingProgress > 0 && (
                                    <div className="mt-4 max-w-xs mx-auto">
                                        <div className="w-full bg-secondary rounded-full h-2">
                                            <div
                                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${processingProgress}%` }}
                                            />
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-2">{processingProgress}% uploaded</p>
                                    </div>
                                )}
                                <p className="text-sm text-muted-foreground mt-1">This may take a few seconds</p>
                            </div>
                        ) : batchMode ? (
                            <div className="py-8">
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <ImageIcon className="w-8 h-8 text-primary" />
                                </div>
                                <p className="text-lg font-medium mb-2">
                                    {selectedFiles.length} image{selectedFiles.length !== 1 ? 's' : ''} selected
                                </p>
                                <p className="text-sm text-muted-foreground mb-6">
                                    {selectedFiles.length <= 5 ? 'Individual downloads available' : 'Will be downloaded as ZIP'}
                                </p>
                                <div className="flex gap-3 justify-center">
                                    <Button
                                        onClick={processBatch}
                                        className="rounded-full"
                                        data-testid="process-batch-btn"
                                    >
                                        Process Batch
                                    </Button>
                                    <Button
                                        onClick={cancelBatch}
                                        variant="outline"
                                        className="rounded-full"
                                        data-testid="cancel-batch-btn"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <Upload className="w-8 h-8 text-primary" />
                                </div>
                                <p className="text-lg font-medium mb-2">
                                    Drop your image{isProUser ? '(s)' : ''} here
                                </p>
                                <p className="text-sm text-muted-foreground mb-4">
                                    or click to browse
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Supports JPG, PNG, WebP • Max 10MB{isProUser ? ' • Batch processing available' : ''}
                                </p>
                            </>
                        )}
                    </div>

                    {/* Processed Image Result */}
                    {processedImage && (
                        <div className="glass-card rounded-2xl p-6 mb-8" data-testid="processed-result">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-heading font-semibold text-xl">Result</h2>
                                <Button
                                    onClick={downloadImage}
                                    className="rounded-full"
                                    data-testid="download-btn"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download PNG
                                </Button>
                            </div>
                            <div className="relative rounded-xl overflow-hidden bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3C%2Fsvg%3E')]">
                                <img
                                    src={processedImage}
                                    alt="Processed"
                                    className="w-full max-h-[500px] object-contain"
                                    data-testid="processed-image"
                                />
                            </div>
                        </div>
                    )}

                    {/* Batch Results */}
                    {batchResults && (
                        <div className="glass-card rounded-2xl p-6 mb-8" data-testid="batch-results">
                            <div className="mb-6">
                                <h2 className="font-heading font-semibold text-xl mb-2">Batch Results</h2>
                                <p className="text-sm text-muted-foreground">
                                    {batchResults.successful} successful, {batchResults.failed} failed out of {batchResults.total_images} images
                                </p>
                            </div>

                            {/* Individual downloads (≤5 images) */}
                            {batchResults.download_type === 'individual' && (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                                        {batchResults.results.filter(r => r.success).map((result, index) => (
                                            <div key={index} className="space-y-2">
                                                <div className="relative rounded-xl overflow-hidden bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f0f0f0%22%2F%3E%3C%2Fsvg%3E')] aspect-square">
                                                    <img
                                                        src={result.image_data}
                                                        alt={result.original_filename}
                                                        className="w-full h-full object-contain"
                                                    />
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="w-full rounded-full text-xs"
                                                    onClick={() => downloadIndividualImage(result.image_data, result.original_filename)}
                                                >
                                                    <Download className="w-3 h-3 mr-1" />
                                                    Download
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    {batchResults.zip_data && (
                                        <div className="pt-4 border-t border-border">
                                            <Button
                                                onClick={() => downloadBatchZip(batchResults.zip_data)}
                                                className="rounded-full w-full sm:w-auto"
                                                data-testid="download-all-zip-btn"
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                Download All as ZIP
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* ZIP download only (>5 images) */}
                            {batchResults.download_type === 'zip' && batchResults.zip_data && (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                        <Download className="w-8 h-8 text-primary" />
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        {batchResults.successful} images ready for download
                                    </p>
                                    <Button
                                        onClick={() => downloadBatchZip(batchResults.zip_data)}
                                        className="rounded-full"
                                        size="lg"
                                        data-testid="download-zip-btn"
                                    >
                                        <Download className="w-4 h-4 mr-2" />
                                        Download ZIP ({batchResults.successful} images)
                                    </Button>
                                </div>
                            )}

                            {/* Failed images */}
                            {batchResults.failed > 0 && (
                                <div className="mt-6 pt-6 border-t border-border">
                                    <h3 className="font-medium text-sm mb-3 text-destructive">Failed Images</h3>
                                    <div className="space-y-2">
                                        {batchResults.results.filter(r => !r.success).map((result, index) => (
                                            <div key={index} className="text-xs text-muted-foreground bg-destructive/10 rounded-lg p-3">
                                                <p className="font-medium text-destructive">{result.original_filename}</p>
                                                <p className="mt-1">{result.error}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 pt-6 border-t border-border">
                                <Button
                                    onClick={cancelBatch}
                                    variant="ghost"
                                    className="w-full rounded-full"
                                >
                                    Process More Images
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Batch Feature Promo for Free Users */}
                    {!isProUser && !processedImage && !batchResults && (
                        <div className="glass-card rounded-2xl p-6 mb-8 border-2 border-primary/20" data-testid="batch-promo">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <Crown className="w-6 h-6 text-primary" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-heading font-semibold text-lg mb-1">
                                        Unlock Batch Processing
                                    </h3>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Process up to 50 images at once and download as ZIP. Get monthly credits with rollover.
                                    </p>
                                    <ul className="text-xs text-muted-foreground space-y-1 mb-4">
                                        <li>✓ Remove backgrounds from multiple images simultaneously</li>
                                        <li>✓ Automatic ZIP packaging for easy download</li>
                                        <li>✓ No watermarks on processed images</li>
                                        <li>✓ Monthly credits with rollover (unused credits carry over)</li>
                                    </ul>
                                </div>
                                <Button
                                    onClick={() => navigate('/pricing')}
                                    className="rounded-full flex-shrink-0"
                                    data-testid="promo-upgrade-btn"
                                >
                                    <Crown className="w-4 h-4 mr-2" />
                                    Upgrade to Pro
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* History */}
                    {history.length > 0 && (
                        <div>
                            <h2 className="font-heading font-semibold text-xl mb-4">Recent Images</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {history.slice(0, 8).map((image, index) => (
                                    <div 
                                        key={image.id}
                                        className="aspect-square rounded-xl bg-secondary/50 flex items-center justify-center border border-border"
                                        data-testid={`history-item-${index}`}
                                    >
                                        <div className="text-center">
                                            <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-xs text-muted-foreground truncate px-2">
                                                {image.original_filename}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty state for new users */}
                    {history.length === 0 && !processedImage && (
                        <div className="text-center py-12">
                            <ImageIcon className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                            <p className="text-muted-foreground">
                                Your processed images will appear here
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
